class VariationSelector {
	/**
	 * Constructs the VariationSelector class, initializes selectors, binds form events, and applies initial stock logic if applicable.
	 */
	constructor() {
		this.currentlyOpenDropdown = null;
		this.variationForm = document.querySelector( '.variations_form' );

		if ( ! this.variationForm ) {
			return;
		}

		this.variationsData = JSON.parse( this.variationForm.dataset.product_variations );
		this.initSelectors();
		this.bindVariationFormEvents();
		// Determine the number of variation dropdowns
		this.numberOfVariationSelects = this.variationForm.querySelectorAll( 'select' ).length;
	}

	/**
	 * Initialises selectors for the variation form and toggle buttons, setting up the necessary event listeners.
	 */
	initSelectors() {
		this.variationSelects = this.variationForm.querySelectorAll( 'select' );
		document.querySelectorAll( '.variation__toggle' ).forEach( toggleButton => {
			this.initEvents( toggleButton );
		} );
	}

	/**
	 * Binds change event listeners to variation select elements to update variation availability.
	 */
	bindVariationFormEvents() {
		this.variationSelects.forEach( select => {
			select.addEventListener( 'change', () => {
				// Nothing yet.
			} );
		} );
	}

	/**
	 * Updates the availability of variation items based on the current selections in the variation form.
	 */
	updateVariationAvailability() {
		const selectedAttributes = {};
		this.variationSelects.forEach( select => {
			if ( select.value ) {
				selectedAttributes[ select.name ] = select.value;
			}
		} );

		document.querySelectorAll( '.variation__item' ).forEach( item => {
			const attributeName = item.dataset.attributeName;
			const attributeValue = item.dataset.attributeValue;
			const isAvailable = this.checkVariationAvailability( attributeName, attributeValue, selectedAttributes );
			item.dataset.inStock = isAvailable ? 'true' : 'false';
		} );
	}

	/**
	 * Checks if a given variation is available based on the selected attributes.
	 *
	 * @param {string} attributeName      - The name of the attribute to check.
	 * @param {string} attributeValue     - The value of the attribute to check.
	 * @param {Object} selectedAttributes - An object containing the currently selected attributes.
	 * @return {boolean} True if the variation is available, false otherwise.
	 */
	checkVariationAvailability( attributeName, attributeValue, selectedAttributes ) {
		for ( const variation of this.variationsData ) {
			if ( variation.attributes[ attributeName ] === attributeValue || variation.attributes[ attributeName ] === '' ) {
				let match = true;
				for ( const key in selectedAttributes ) {
					if ( selectedAttributes.hasOwnProperty( key ) && variation.attributes[ key ] !== undefined ) {
						if ( variation.attributes[ key ] !== selectedAttributes[ key ] && variation.attributes[ key ] !== '' ) {
							match = false;
							break;
						}
					}
				}
				if ( match && variation.is_in_stock ) {
					return true;
				}
			}
		}
		return false;
	}

	/**
	 * Initializes event listeners for a given toggle button and its associated listbox.
	 *
	 * @param {HTMLElement} toggleButton - The toggle button element to initialize events for.
	 */
	initEvents( toggleButton ) {
		const listbox = toggleButton.nextElementSibling;

		toggleButton.addEventListener( 'click', () => {
			const expanded = toggleButton.getAttribute( 'aria-expanded' ) === 'true';
			if ( this.currentlyOpenDropdown && this.currentlyOpenDropdown !== listbox && this.currentlyOpenDropdown.style.display !== 'none' ) {
				this.closeDropdown( this.currentlyOpenDropdown );
			}
			toggleButton.setAttribute( 'aria-expanded', ! expanded );
			listbox.style.display = ! expanded ? 'block' : 'none';
			this.currentlyOpenDropdown = ! expanded ? listbox : null;
		} );

		listbox.querySelectorAll( '.variation__item' ).forEach( item => {
			item.addEventListener( 'click', e => {
				this.selectItem( e.target, toggleButton, listbox );
			} );
		} );
	}

	/**
	 * Handles the selection of a variation item, updating the display and triggering a change event on the corresponding select element.
	 *
	 * @param {HTMLElement} item         - The selected item element.
	 * @param {HTMLElement} toggleButton - The toggle button associated with the item's listbox.
	 * @param {HTMLElement} listbox      - The listbox containing the item.
	 */
	selectItem( item, toggleButton, listbox ) {
		const selectedItem = item.closest( '.variation__item' );
		const text = selectedItem.querySelector( '.variation__item-text' ).textContent;
		const swatch = selectedItem.querySelector( '.variation__swatch' ) ? selectedItem.querySelector( '.variation__swatch' ).cloneNode( true ) : null;

		toggleButton.querySelector( '.variation__item-text' ).textContent = text;
		this.updateSwatchDisplay( toggleButton, swatch );

		toggleButton.setAttribute( 'aria-expanded', 'false' );
		listbox.style.display = 'none';

		listbox.querySelectorAll( '.variation__checkmark' ).forEach( checkmark => {
			checkmark.classList.remove( 'variation__checkmark--visible' );
		} );

		const checkmark = selectedItem.querySelector( '.variation__checkmark' );
		if ( checkmark ) {
			checkmark.classList.add( 'variation__checkmark--visible' );
		}

		const attributeName = selectedItem.dataset.attributeName;
		const attributeValue = selectedItem.dataset.attributeValue;

		const select = document.querySelector( `select[name="${ attributeName }"]` );
		if ( select ) {
			select.value = attributeValue;
			// Trigger a jQuery change event
			jQuery( select ).trigger( 'change' );
		}
	}

	/**
	 * Updates the display of a swatch on a toggle button.
	 *
	 * @param {HTMLElement}      toggleButton - The toggle button to update the swatch display for.
	 * @param {HTMLElement|null} swatch       - The swatch element to display, or null if no swatch is to be displayed.
	 */
	updateSwatchDisplay( toggleButton, swatch ) {
		const existingSwatch = toggleButton.querySelector( '.variation__swatch' );
		if ( existingSwatch ) {
			existingSwatch.remove();
		}

		if ( swatch ) {
			const textElement = toggleButton.querySelector( '.variation__item-text' );
			if ( textElement ) {
				textElement.parentNode.insertBefore( swatch, textElement );
			} else {
				toggleButton.appendChild( swatch );
			}
		}
	}

	/**
	 * Closes the dropdown listbox.
	 *
	 * @param {HTMLElement} listbox - The listbox to close.
	 */
	closeDropdown( listbox ) {
		const toggleButton = listbox.previousElementSibling;
		listbox.style.display = 'none';
		toggleButton.setAttribute( 'aria-expanded', 'false' );
	}
}

// Export the class
export default VariationSelector;
